import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiClock
} from 'react-icons/fi';
import AppointmentService from '../../../../AppointmentTimeline/services/appointmentTimelineService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import AppointmentTreatmentTable from './AppointmentTreatmentTable';
import AddAppointmentTreatment from './AddAppointmentTreatment';

const AppointmentTreatments = ({ onCountUpdate, componentName = 'AppointmentTreatments' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    appointmentType: '',
    status: '',
    doctorName: '',
    appointmentDate: '',
    endDate: '',
    priority: 'All'
  });

  // Instancia del servicio
  const appointmentService = new AppointmentService();

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**

const loadAppointments = useCallback(async () => {
  if (!patientId) {
    console.log('No patientId provided, component will show empty state');
    setAppointments([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
  
  setIsLoading(true);
  try {
    console.log('Loading appointments for patient:', patientId);
    const response = await appointmentService.getPatientAppointments(patientId);
    console.log('Raw API response:', response);
    
    let appointmentsData = [];
    
    // Extraer datos de la respuesta de la API
    if (response.success && response.data) {
      appointmentsData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (response.data) {
      appointmentsData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (Array.isArray(response)) {
      appointmentsData = response;
    } else if (response.appointments) {
      appointmentsData = Array.isArray(response.appointments) ? response.appointments : [response.appointments];
    }

    console.log('Extracted appointments data:', appointmentsData);

    // Los datos ya vienen con la estructura correcta de la API, no necesitan normalización excesiva
    const processedAppointments = appointmentsData.map((appointment, index) => {
      if (index < 2) {
        console.log(`Processing appointment ${index}:`, appointment);
      }
      
      return {
        // Mantener datos originales tal como vienen de la API
        ...appointment,
        
        // Solo agregar campos calculados o de conveniencia
        patient_id: appointment.userid || patientId,
        doctor_id: appointment.meetinguserid?._id,
        
        // Campos de conveniencia para la tabla
        consultation_started: appointment.consultationStartTime ? true : false,
        consultation_completed: appointment.status === 'Completed',
        checked_in: appointment.checkedIn || false,
        
        // Preservar original para vista detallada
        _original: appointment
      };
    });

    console.log('Processed appointments:', processedAppointments);
    
    // Actualizar contador
    onCountUpdate && onCountUpdate(componentName, processedAppointments.length);
    
    setAppointments(processedAppointments);
  } catch (error) {
    console.error('Error loading appointments:', error);
    setAppointments([]);
    // También actualizar contador en caso de error
    onCountUpdate && onCountUpdate(componentName, 0);
  } finally {
    setIsLoading(false);
  }
}, [patientId, onCountUpdate, componentName, patientData?.patientName]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadAppointmentStats = useCallback(async () => {
    if (!patientId || appointments.length === 0) return;
    
    try {
      const urgentCount = appointments.filter(appointment => 
        appointment.is_urgent || appointment.priority?.toLowerCase() === 'urgent'
      ).length;
      
      const pendingCount = appointments.filter(appointment => 
        appointment.status?.toLowerCase() === 'pending'
      ).length;
      
      const todayAppointments = appointments.filter(appointment => {
        const appointmentDate = new Date(appointment.appointment_date);
        const today = new Date();
        return appointmentDate.toDateString() === today.toDateString();
      }).length;
      
      if (urgentCount > 0 || pendingCount > 0 || todayAppointments > 0) {
        setAlertInfo({
          alert_type: 'appointment',
          message: `${pendingCount} pending appointments${urgentCount > 0 ? `, ${urgentCount} urgent` : ''}${todayAppointments > 0 ? `, ${todayAppointments} today` : ''}`,
          priority: urgentCount > 0 ? 'high' : todayAppointments > 0 ? 'medium' : 'low',
          requires_action: urgentCount > 0 || todayAppointments > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading appointment stats:', error);
      setAlertInfo(null);
    }
  }, [patientId]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Appointments array length:', appointments.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...appointments];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(appointment => 
        (appointment.appointmentId || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (appointment.doctor_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (appointment.reason_for_visit || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (appointment.status || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (appointment.appointment_type || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.priority !== 'All') {
      if (filters.priority === 'Urgent') {
        filtered = filtered.filter(appointment => appointment.is_urgent || appointment.priority?.toLowerCase() === 'urgent');
      } else if (filters.priority === 'Normal') {
        filtered = filtered.filter(appointment => appointment.priority?.toLowerCase() === 'normal');
      }
    }

    if (filters.appointmentType) {
      filtered = filtered.filter(appointment => 
        (appointment.appointment_type || '').toLowerCase().includes(filters.appointmentType.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(appointment => 
        (appointment.status || '').toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.doctorName) {
      filtered = filtered.filter(appointment => 
        (appointment.doctor_name || '').toLowerCase().includes(filters.doctorName.toLowerCase())
      );
    }

    if (filters.appointmentDate) {
      filtered = filtered.filter(appointment => {
        const appointmentDate = appointment.appointment_date;
        return appointmentDate && new Date(appointmentDate) >= new Date(filters.appointmentDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(appointment => {
        const appointmentDate = appointment.appointment_date;
        return appointmentDate && new Date(appointmentDate) <= new Date(filters.endDate);
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.appointment_date || item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'priority': {
          const priorityOrder = { 'urgent': 3, 'high': 2, 'normal': 1, 'low': 0 };
          const priorityA = (a.priority || 'normal').toLowerCase();
          const priorityB = (b.priority || 'normal').toLowerCase();
          return (priorityOrder[priorityB] || 0) - (priorityOrder[priorityA] || 0);
        }
        case 'alphabetical':
          const doctorA = (a.doctor_name || '').toLowerCase();
          const doctorB = (b.doctor_name || '').toLowerCase();
          return doctorA.localeCompare(doctorB);
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredAppointments(filtered);
  }, [appointments, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'priority': return 'By Priority';
      case 'alphabetical': return 'By Doctor A-Z';
      default: return 'Sort by';
    }
  };

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadAppointments();
    }
  }, [patientId, loadAppointments]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
  if (appointments.length > 0) {
    loadAppointmentStats();
  }
}, [appointments.length, loadAppointmentStats]);

  

  // Apply filters whenever appointments or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- appointments.length:', appointments.length);
    console.log('- sortBy:', sortBy);
    
    if (appointments.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay appointments, limpiando filteredAppointments');
      setFilteredAppointments([]);
    }
  }, [appointments, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas]);

  // Solo mostrar loading si estamos esperando datos específicos
  if (isLoading && patientId) {
    return <div>Loading patient data...</div>;
  }

  const handleViewAppointment = (appointmentId) => {
    console.log('Viewing appointment:', appointmentId);
    const appointment = appointments.find(a => a.appointment_id === appointmentId);
    if (appointment) {
      console.log('Found appointment for viewing:', appointment);
      setSelectedAppointment(appointment);
      setShowViewOffcanvas(true);
    } else {
      console.error('Appointment not found:', appointmentId);
    }
  };
  
  const handleEditAppointment = (appointmentId) => {
    console.log('Editing appointment:', appointmentId);
    const appointment = appointments.find(a => a.appointment_id === appointmentId);
    if (appointment) {
      console.log('Found appointment for editing:', appointment);
      setEditingAppointment(appointment);
      setShowEditOffcanvas(true);
    } else {
      console.error('Appointment not found for editing:', appointmentId);
    }
  };

  const handleAddNew = () => {
  console.log('Add New button clicked - opening modal');
  setShowFilterOffcanvas(false);
  setShowViewOffcanvas(false);
  setShowAddOffcanvas(true);
};

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await appointmentService.deleteAppointment(appointmentId);
        await loadAppointments();
        await loadAppointmentStats();
      } catch (error) {
        console.error('Error deleting appointment:', error);
        alert('Error deleting appointment. Please try again.');
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      appointmentType: '',
      status: '',
      doctorName: '',
      appointmentDate: '',
      endDate: '',
      priority: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedAppointment(null);
  };

  const handleCloseAddOffcanvas = () => {
  console.log('Closing add offcanvas');
  if (!isSubmitting) {
    setShowAddOffcanvas(false);
  }
};

  const handleCloseEditOffcanvas = () => {
    console.log('Closing edit offcanvas');
    if (!isSubmitting) {
      setShowEditOffcanvas(false);
      setEditingAppointment(null);
    }
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleOpenFilterOffcanvas = () => {
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'secondary';
      case 'waiting':
        return 'warning';
      case 'called':
        return 'info';
      case 'in consultation':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'no show':
        return 'dark';
      case 'confirmed':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getPriorityColor = (priority, isUrgent) => {
    if (isUrgent) return 'danger';
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'danger';
      case 'high':
        return 'warning';
      case 'normal':
        return 'success';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  // Active filters logic (similar to AllergiesTreatments)
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.priority !== 'All') {
      activeFilters.push({
        key: 'priority',
        label: `Priority: ${filters.priority}`,
        value: filters.priority,
        type: 'priority'
      });
    }
    
    if (filters.appointmentType) {
      activeFilters.push({
        key: 'appointmentType',
        label: `Type: ${filters.appointmentType}`,
        value: filters.appointmentType,
        type: 'appointmentType'
      });
    }
    
    if (filters.status) {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.doctorName) {
      activeFilters.push({
        key: 'doctorName',
        label: `Doctor: ${filters.doctorName}`,
        value: filters.doctorName,
        type: 'doctorName'
      });
    }
    
    if (filters.appointmentDate) {
      activeFilters.push({
        key: 'appointmentDate',
        label: `From: ${formatDate(filters.appointmentDate)}`,
        value: filters.appointmentDate,
        type: 'appointmentDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'priority':
        setFilters(prev => ({ ...prev, priority: 'All' }));
        break;
      case 'appointmentType':
        setFilters(prev => ({ ...prev, appointmentType: '' }));
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: '' }));
        break;
      case 'doctorName':
        setFilters(prev => ({ ...prev, doctorName: '' }));
        break;
      case 'appointmentDate':
        setFilters(prev => ({ ...prev, appointmentDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      appointmentType: '',
      status: '',
      doctorName: '',
      appointmentDate: '',
      endDate: '',
      priority: 'All'
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-appointments'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Appointments {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search appointments"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'priority' ? 'active' : ''}`}
                          onClick={() => handleSortChange('priority')}
                        >
                          By Priority
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          By Doctor A-Z
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
            
                  {/* Add New Button */}
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleAddNew}
                  >
                    <FiPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table Component */}
          <AppointmentTreatmentTable
            appointments={filteredAppointments}
            onViewAppointment={handleViewAppointment}
            onEditAppointment={handleEditAppointment}
            onDeleteAppointment={handleDeleteAppointment}
            onReloadAppointments={loadAppointments}
            isLoading={isLoading}
            globalFilter={globalFilter}
            onGlobalFilterChange={handleGlobalFilterChange}
            patientData={patientData}
          />


          {/* Results Summary */}
          {filteredAppointments.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredAppointments.length} of {appointments.length} appointments
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Appointments</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Priority Filter */}
                <div className="mb-3">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Normal">Normal</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                {/* Appointment Type Filter */}
                <div className="mb-3">
                  <label className="form-label">Appointment Type</label>
                  <select
                    className="form-select"
                    value={filters.appointmentType}
                    onChange={(e) => handleFilterChange('appointmentType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="consultation">Consultation</option>
                    <option value="follow-up">Follow-up</option>
                    <option value="check-up">Check-up</option>
                    <option value="emergency">Emergency</option>
                    <option value="procedure">Procedure</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="waiting">Waiting</option>
                    <option value="called">Called</option>
                    <option value="in consultation">In Consultation</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no show">No Show</option>
                  </select>
                </div>

                {/* Doctor Name Filter */}
                <div className="mb-3">
                  <label className="form-label">Doctor Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter doctor name"
                    value={filters.doctorName}
                    onChange={(e) => handleFilterChange('doctorName', e.target.value)}
                  />
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Appointment From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.appointmentDate}
                    onChange={(e) => handleFilterChange('appointmentDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Appointment To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Appointment Offcanvas */}
      {showViewOffcanvas && selectedAppointment && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Appointment Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Appointment Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Appointment ID</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.appointmentId || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Type</label>
                          <p className="form-control-plaintext">
                            <span className="badge bg-info">
                              {selectedAppointment.appointment_type || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getStatusColor(selectedAppointment.status)}`}>
                              {selectedAppointment.status || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Priority</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getPriorityColor(selectedAppointment.priority, selectedAppointment.is_urgent)}`}>
                              {selectedAppointment.is_urgent ? 'URGENT' : selectedAppointment.priority || 'Normal'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Date
                          </label>
                          <p className="form-control-plaintext">
                            {formatDate(selectedAppointment.appointment_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiClock className="me-1" />
                            Time
                          </label>
                          <p className="form-control-plaintext">
                            {formatTime(selectedAppointment.appointment_time)}
                            {selectedAppointment.end_time && ` - ${formatTime(selectedAppointment.end_time)}`}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Duration</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.duration} minutes
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Room</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.room_number || 'TBD'}
                          </p>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-bold">Reason for Visit</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.reason_for_visit || 'Not specified'}
                          </p>
                        </div>
                        {selectedAppointment.symptoms && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Symptoms</label>
                            <p className="form-control-plaintext">
                              {selectedAppointment.symptoms}
                            </p>
                          </div>
                        )}
                        {selectedAppointment.notes && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Notes</label>
                            <p className="form-control-plaintext">
                              {selectedAppointment.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Doctor Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Doctor Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiUser className="me-1" />
                            Doctor Name
                          </label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.doctor_name || 'Not assigned'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Specialization</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.doctor_specialization || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Consultation Status */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Consultation Status</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Checked In</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${selectedAppointment.checked_in ? 'success' : 'secondary'}`}>
                              {selectedAppointment.checked_in ? 'Yes' : 'No'}
                            </span>
                          </p>
                          {selectedAppointment.checked_in_at && (
                            <small className="text-muted">
                              at {formatDateTime(selectedAppointment.checked_in_at)}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Consultation Started</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${selectedAppointment.consultation_started ? 'success' : 'secondary'}`}>
                              {selectedAppointment.consultation_started ? 'Yes' : 'No'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Consultation Completed</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${selectedAppointment.consultation_completed ? 'success' : 'secondary'}`}>
                              {selectedAppointment.consultation_completed ? 'Yes' : 'No'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Estimated Wait Time</label>
                          <p className="form-control-plaintext">
                            {selectedAppointment.estimated_wait_time} minutes
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Timeline</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Created</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedAppointment.created_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Last Updated</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedAppointment.updated_date)}
                          </p>
                        </div>
                        {selectedAppointment.cancelled && (
                          <>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">Cancelled At</label>
                              <p className="form-control-plaintext">
                                {formatDateTime(selectedAppointment.cancelled_at)}
                              </p>
                            </div>
                            <div className="col-12">
                              <label className="form-label fw-bold">Cancellation Reason</label>
                              <p className="form-control-plaintext">
                                {selectedAppointment.cancellation_reason || 'Not specified'}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add New Appointment Offcanvas */}
{showAddOffcanvas && (
  <AddAppointmentTreatment
    isOpen={showAddOffcanvas}
    onClose={handleCloseAddOffcanvas}
    patientData={patientData}
    onAppointmentCreated={loadAppointments}
    isSubmitting={isSubmitting}
    setIsSubmitting={setIsSubmitting}
  />
)}

      {/* Edit Appointment Offcanvas */}
      {showEditOffcanvas && editingAppointment && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseEditOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Edit Appointment</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseEditOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              {/* Aquí iría el EditAppointmentForm component */}
              <div className="alert alert-info">
                <FiInfo className="me-2" />
                Edit Appointment functionality will be implemented here.
              </div>
            </div>
          </div>
        </>
      )}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

export default AppointmentTreatments;